import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Info, Loader2, Trash2, Plus, Clock } from 'lucide-react';
import { adminMonitoring, isBlindedStudyType, studies, type MonitorAssignmentRow } from '@/api/client';
import { fmtDateTime } from '@/lib/dates';
import { humanRole } from '@/lib/humanise';
import { REVIEWER_SITE_ROLES } from '@/lib/siteRoles';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

/**
 * F4 - Admin surface for managing time-bound monitor assignments.
 *
 * Every assignment MUST carry a window_start_at and window_end_at; the
 * form disables submit until both are set and start is strictly before
 * end. Existing rows with null bounds render an "Open-ended (legacy)"
 * badge; new ones always have a window.
 *
 * 13 Oct 2026 (Harish): this is the ONLY place a monitor or auditor is put on
 * a study — Invite and Manage staff no longer offer those roles. Flow:
 * invite as Site User → Site Team › Change role to Monitor/Auditor → assign
 * here. Who sees the page is the RBAC grid's compliance.monitor_assignments
 * (sidebar + route via PAGE_TO_OPERATION). Works on the site chosen in the
 * header, like every other page.
 */
export function MonitorAssignmentsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const siteId = user?.activeSiteId ?? '';
  const siteName = user?.siteNames?.[siteId] ?? 'this site';
  const [showCreate, setShowCreate] = useState(false);

  const assignmentsQ = useQuery({
    queryKey: ['admin', 'monitor-assignments', siteId],
    queryFn: () => adminMonitoring.list(siteId),
    enabled: !!siteId,
  });

  const rows = assignmentsQ.data?.rows ?? [];
  // Current = not revoked and not past its window (active or scheduled);
  // revoked and expired move to History, as on the Blinding page.
  const currentRows = rows.filter((r) => {
    const s = assignmentStatus(r).key;
    return s === 'active' || s === 'scheduled';
  });
  const historyRows = rows.filter((r) => !currentRows.includes(r));

  const revokeMut = useMutation({
    mutationFn: (id: string) => adminMonitoring.revoke(id),
    onSuccess: () => {
      toast.success('Assignment revoked.');
      qc.invalidateQueries({ queryKey: ['admin', 'monitor-assignments'] });
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to revoke.'));
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/dashboard"
        title="Monitor assignments"
        subtitle={`Put a monitor or auditor on a study at ${siteName} for a date/time window. This is the only place reviewers are added to a study. Access outside the window is denied and audited.`}
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            disabled={!siteId}
            className="btn-primary disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            New assignment
          </button>
        }
      />

      {assignmentsQ.isLoading && (
        <div className="card flex items-center justify-center gap-2 p-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading assignments…
        </div>
      )}

      {assignmentsQ.isError && (
        <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(assignmentsQ.error)}`}>
          {isPermissionError(assignmentsQ.error)
            ? <Info className="mt-0.5 h-4 w-4 shrink-0" />
            : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{errorText(assignmentsQ.error, 'Could not load assignments.')}</span>
        </div>
      )}

      {assignmentsQ.data && (
        <>
          <AssignmentsSection
            title={`Current (${currentRows.length})`}
            rows={currentRows}
            emptyText={`No current monitor assignments at ${siteName}.`}
            onRevoke={(a) => {
              if (confirm(`Revoke assignment ${a.reference_number}?`)) revokeMut.mutate(a.assignment_id);
            }}
            revoking={revokeMut.isPending}
          />
          {historyRows.length > 0 && (
            <AssignmentsSection
              title={`History (${historyRows.length})`}
              rows={historyRows}
              emptyText=""
              onRevoke={null}
              revoking={false}
            />
          )}
        </>
      )}

      {showCreate && siteId && (
        <NewAssignmentDialog
          siteId={siteId}
          siteName={siteName}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['admin', 'monitor-assignments'] });
            toast.success('Assignment created.');
          }}
        />
      )}
    </div>
  );
}

type AssignmentStatusKey = 'active' | 'scheduled' | 'expired' | 'revoked';

/** Active only while the window is open; revoked and expired rows say so. */
function assignmentStatus(row: MonitorAssignmentRow): { key: AssignmentStatusKey; label: string; badge: string } {
  if (row.active !== 'true') return { key: 'revoked', label: 'Revoked', badge: 'badge-neutral' };
  const now = Date.now();
  if (row.window_end_at && new Date(row.window_end_at).getTime() <= now) {
    return { key: 'expired', label: 'Expired', badge: 'badge-neutral' };
  }
  if (row.window_start_at && new Date(row.window_start_at).getTime() > now) {
    return { key: 'scheduled', label: 'Scheduled', badge: 'badge-info' };
  }
  return { key: 'active', label: 'Active', badge: 'badge-success' };
}

/** Same layout as the Blinding page's sections: titled card, muted header row. */
function AssignmentsSection({
  title,
  rows,
  emptyText,
  onRevoke,
  revoking,
}: {
  title: string;
  rows: MonitorAssignmentRow[];
  emptyText: string;
  onRevoke: ((row: MonitorAssignmentRow) => void) | null;
  revoking: boolean;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-surface-border bg-surface-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-ink-muted">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/30 text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Monitor</th>
                <th className="px-3 py-2 font-medium">Study</th>
                <th className="px-3 py-2 font-medium">Blinding</th>
                <th className="px-3 py-2 font-medium">Subjects</th>
                <th className="px-3 py-2 font-medium">Window</th>
                <th className="px-3 py-2 font-medium">Status</th>
                {onRevoke && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const status = assignmentStatus(a);
                return (
                  <tr key={a.assignment_id} className="border-b border-surface-border last:border-0 hover:bg-surface-muted/30">
                    <td className="px-3 py-2">
                      <div className="text-ink-primary">{a.monitor_name || a.monitor_email || 'Unknown user'}</div>
                      <div className="text-[11px] text-ink-muted">
                        {a.monitor_name && a.monitor_email ? `${a.monitor_email} · ` : ''}
                        <span className="font-mono">{a.reference_number}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-secondary">
                      <div className="text-ink-primary">{a.study_protocol_number ?? '—'}</div>
                      {a.study_title && <div className="text-[11px] text-ink-muted">{a.study_title}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {a.blinding === 'unblinded' ? (
                        <span className="badge badge-success">Unblinded</span>
                      ) : a.blinding === 'blinded' ? (
                        <span className="badge badge-warning">Blinded</span>
                      ) : (
                        <span className="text-ink-muted">Open label</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-secondary">
                      {a.subject_id ? (
                        <span className="font-mono">{a.subject_reference ?? a.subject_id}</span>
                      ) : (
                        'All subjects in this study'
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-secondary">
                      {a.window_start_at && a.window_end_at ? (
                        <>
                          <div>{fmtDateTime(a.window_start_at)}</div>
                          <div className="text-[11px] text-ink-muted">to {fmtDateTime(a.window_end_at)}</div>
                        </>
                      ) : (
                        <span className="badge badge-warning">Open-ended (legacy)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`badge ${status.badge}`}>{status.label}</span>
                    </td>
                    {onRevoke && (
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-xs text-ink-primary hover:bg-surface-muted/50 disabled:opacity-50"
                          onClick={() => onRevoke(a)}
                          disabled={revoking}
                          title="End this assignment now"
                        >
                          <Trash2 className="h-3 w-3" /> Revoke
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------
function NewAssignmentDialog({
  siteId,
  siteName,
  onClose,
  onCreated,
}: {
  siteId: string;
  siteName: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [monitorUserId, setMonitorUserId] = useState('');
  const [studyId, setStudyId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [blinding, setBlinding] = useState<'' | 'blinded' | 'unblinded'>('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Same permission as the rest of this page (compliance.monitor_assignments)
  // — not the platform user list, which a Coordinator granted this page can't
  // read (15 Oct 2026).
  const candidatesQ = useQuery({
    queryKey: ['admin', 'monitor-assignment-candidates', siteId],
    queryFn: () => adminMonitoring.candidates(siteId),
  });
  const studiesQ = useQuery({
    queryKey: ['studies-list'],
    queryFn: () => studies.list(),
  });
  // Only the chosen study's subjects at this site.
  const subjectsQ = useQuery({
    queryKey: ['admin', 'monitor-assignment-subjects', siteId, studyId],
    queryFn: () => adminMonitoring.subjects(studyId, siteId),
    enabled: !!studyId,
  });

  // Only people whose role AT THIS SITE is Monitor or Auditor — the server
  // returns no one else and refuses anyone else. A Site User is made one on
  // Site Team › Change role.
  const monitorOptions = (candidatesQ.data?.candidates ?? []).filter((c) =>
    REVIEWER_SITE_ROLES.includes(c.role),
  );
  const studyOptions = studiesQ.data?.rows ?? [];
  const study = studyOptions.find((s) => s.study_id === studyId);
  const studyBlinded = isBlindedStudyType(study?.blinding_type);

  const startEndOk =
    windowStart && windowEnd && new Date(windowStart).getTime() < new Date(windowEnd).getTime();
  const canSubmit = !!monitorUserId && !!studyId && startEndOk && (!studyBlinded || !!blinding);

  const createMut = useMutation({
    mutationFn: () =>
      adminMonitoring.create({
        monitor_user_id: monitorUserId,
        study_id: studyId,
        site_id: siteId,
        subject_id: subjectId || undefined,
        // datetime-local emits `YYYY-MM-DDTHH:mm` (no seconds, no TZ).
        // The BE requires an ISO 8601 datetime — Date.toISOString() gives us
        // `YYYY-MM-DDTHH:mm:00.000Z` from the browser's local time.
        window_start_at: new Date(windowStart).toISOString(),
        window_end_at: new Date(windowEnd).toISOString(),
        blinded: studyBlinded ? blinding === 'blinded' : undefined,
      }),
    onSuccess: () => onCreated(),
    onError: (err) => setError(errorText(err, 'Could not create.')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-text-1/40 backdrop-blur-sm !m-0">
      <div className="bg-white rounded-md shadow-modal max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 pt-5 pb-4 border-b border-border-soft">
          <div className="font-bold text-base text-text-1 flex items-center gap-2">
            <Clock size={16} className="text-navy" />
            New monitor assignment
          </div>
          <div className="text-xs text-text-3 mt-1">
            At {siteName}. Access is granted only inside the date-time window.
          </div>
        </div>
        <form
          className="px-6 py-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            createMut.mutate();
          }}
        >
          <label className="block">
            <span className="block text-[11.5px] font-semibold text-text-2 mb-1">Monitor / Auditor</span>
            <select
              value={monitorUserId}
              onChange={(e) => setMonitorUserId(e.target.value)}
              className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm"
              required
            >
              <option value="">
                {candidatesQ.isLoading ? 'Loading…' : 'Choose a monitor or auditor…'}
              </option>
              {monitorOptions.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name || u.email} · {u.email} · {humanRole(u.role)}
                </option>
              ))}
            </select>
            {candidatesQ.isError && (
              <span className="mt-1 block text-[11px] text-danger-700">
                {errorText(candidatesQ.error, 'Could not load the monitors and auditors at this site.')}
              </span>
            )}
            {candidatesQ.isSuccess && monitorOptions.length === 0 && (
              <span className="mt-1 block text-[11px] text-text-3">
                No Monitors or Auditors at {siteName}. Invite them as Site User, then change their role on{' '}
                <Link to="/team" className="underline">Site Team</Link>.
              </span>
            )}
          </label>

          <label className="block">
            <span className="block text-[11.5px] font-semibold text-text-2 mb-1">Study</span>
            <select
              value={studyId}
              onChange={(e) => { setStudyId(e.target.value); setBlinding(''); setSubjectId(''); }}
              className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm"
              required
            >
              <option value="">Choose a study…</option>
              {studyOptions.map((s) => (
                <option key={s.study_id} value={s.study_id}>
                  {studyLabel(s)}
                </option>
              ))}
            </select>
          </label>

          {studyBlinded && (
            <fieldset className="block">
              <legend className="block text-[11.5px] font-semibold text-text-2 mb-1">
                Blinding on this study *
              </legend>
              <div className="grid grid-cols-2 gap-2">
                <label className={`rounded-sm border px-3 py-2 text-[12.5px] cursor-pointer ${blinding === 'blinded' ? 'border-navy bg-soft' : 'border-border-strong'}`}>
                  <input type="radio" name="blinding" className="mr-2" checked={blinding === 'blinded'}
                    onChange={() => setBlinding('blinded')} />
                  Blinded
                  <span className="block text-[11px] text-text-3">Blinded eISF and eSource only</span>
                </label>
                <label className={`rounded-sm border px-3 py-2 text-[12.5px] cursor-pointer ${blinding === 'unblinded' ? 'border-navy bg-soft' : 'border-border-strong'}`}>
                  <input type="radio" name="blinding" className="mr-2" checked={blinding === 'unblinded'}
                    onChange={() => setBlinding('unblinded')} />
                  Unblinded
                  <span className="block text-[11px] text-text-3">Unblinded eISF only, no eSource</span>
                </label>
              </div>
            </fieldset>
          )}

          <label className="block">
            <span className="block text-[11.5px] font-semibold text-text-2 mb-1">
              Subject (optional)
            </span>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              disabled={!studyId}
              className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm disabled:opacity-60"
            >
              <option value="">
                {!studyId
                  ? 'Choose a study first'
                  : subjectsQ.isLoading
                    ? 'Loading subjects…'
                    : 'All subjects in this study'}
              </option>
              {(subjectsQ.data?.subjects ?? []).map((s) => (
                <option key={s.subject_id} value={s.subject_id}>
                  {s.reference_number} · {s.status}
                </option>
              ))}
            </select>
            {studyId && !subjectsQ.isLoading && (subjectsQ.data?.subjects.length ?? 0) === 0 && (
              <span className="mt-1 block text-[11px] text-text-3">No subjects on this study yet.</span>
            )}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11.5px] font-semibold text-text-2 mb-1">
                Access starts *
              </span>
              <input
                type="datetime-local"
                value={windowStart}
                onChange={(e) => setWindowStart(e.target.value)}
                required
                className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[11.5px] font-semibold text-text-2 mb-1">
                Access ends *
              </span>
              <input
                type="datetime-local"
                value={windowEnd}
                onChange={(e) => setWindowEnd(e.target.value)}
                required
                className="w-full h-10 px-3 rounded-sm border border-border-strong text-sm"
              />
            </label>
          </div>
          {windowStart && windowEnd && !startEndOk && (
            <p className="text-[11.5px] text-critical">End must be strictly after start.</p>
          )}

          {error && (
            <div className="text-[11.5px] text-critical bg-critical-soft border border-critical/30 rounded-sm px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="h-9 px-3 rounded-sm text-[12.5px] text-text-2 hover:bg-soft"
              disabled={createMut.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || createMut.isPending}
              className="h-9 px-4 rounded-sm bg-navy text-white text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-60"
            >
              {createMut.isPending && <Loader2 size={14} className="animate-spin" />}
              {createMut.isPending ? 'Creating' : 'Create assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
